import { z } from 'zod'

// --- Event Types ---
export const EventTypeSchema = z.enum(['note', 'call', 'meeting'])
export type EventType = z.infer<typeof EventTypeSchema>

// --- User Reference ---
export const EventUserSchema = z.object({
    email: z.string(),
    name: z.string(),
    uid: z.string(),
})
export type EventUser = z.infer<typeof EventUserSchema>

// --- Revision ---
export const RevisionSchema = z.object({
    body: z.string().nullable().optional(),
    editedAt: z.string(),
    editedBy: EventUserSchema,
    title: z.string().nullable().optional(),
})
export type Revision = z.infer<typeof RevisionSchema>

// --- Type-specific metadata ---
export const CallMetadataSchema = z.object({
    aircallCallId: z.string().optional(),
    direction: z.enum(['inbound', 'outbound']),
    durationSeconds: z.number(),
    phoneNumber: z.string().optional(),
})

export const MeetingMetadataSchema = z.object({
    attendees: z.array(z.string()).optional(),
    endTime: z.string().optional(),
    location: z.string().optional(),
    startTime: z.string().optional(),
})

// --- Event (discriminated union) ---
const BaseEventSchema = z.object({
    body: z.string().nullable().optional(),
    createdAt: z.string(),
    createdBy: EventUserSchema.nullable(),
    crmSourceMetadata: z
        .object({ tab: z.enum(['overview', 'finance']) })
        .nullable()
        .optional(),
    deletedAt: z.string().nullable().optional(),
    deletedBy: EventUserSchema.nullable().optional(),
    id: z.string(),
    pinned: z.boolean().optional(),
    pinnedAt: z.string().nullable().optional(),
    pinnedBy: EventUserSchema.nullable().optional(),
    revisions: z.array(RevisionSchema),
    source: z.enum(['crm']).nullable().optional(),
    title: z.string().nullable().optional(),
})

export const NoteEventSchema = BaseEventSchema.extend({
    type: z.literal('note'),
})

export const CallEventSchema = BaseEventSchema.extend({
    metadata: CallMetadataSchema.optional(),
    type: z.literal('call'),
})

export const MeetingEventSchema = BaseEventSchema.extend({
    metadata: MeetingMetadataSchema.optional(),
    type: z.literal('meeting'),
})

export const EventSchema = z.discriminatedUnion('type', [
    NoteEventSchema,
    CallEventSchema,
    MeetingEventSchema,
])

export type Event = z.infer<typeof EventSchema>

// --- Input schemas ---
export const CreateEventSchema = z
    .object({
        body: z.string().nullable().optional(),
        createdBy: EventUserSchema,
        crmSourceMetadata: z
            .object({ tab: z.enum(['overview', 'finance']) })
            .optional(),
        source: z.enum(['crm']).optional(),
        title: z.string().nullable().optional(),
        type: z.enum(['note', 'call', 'meeting']),
    })
    .refine(
        data =>
            (data.title && data.title.length > 0) ||
            (data.body && data.body.length > 0),
        {
            message: 'At least one of title or body must be non-empty',
        },
    )
export type CreateEvent = z.infer<typeof CreateEventSchema>

export const UpdateEventSchema = z
    .object({
        body: z.string().nullable().optional(),
        editedBy: EventUserSchema,
        title: z.string().nullable().optional(),
    })
    .refine(
        data => {
            const titleProvided = data.title !== undefined
            const bodyProvided = data.body !== undefined
            if (titleProvided && bodyProvided) {
                const { title, body } = data
                return (
                    (title !== null &&
                        title !== undefined &&
                        title.length > 0) ||
                    (body !== null && body !== undefined && body.length > 0)
                )
            }
            return true
        },
        {
            message:
                'When both title and body are provided, at least one must be non-empty',
        },
    )
export type UpdateEvent = z.infer<typeof UpdateEventSchema>

export const SoftDeleteEventSchema = z.object({
    deletedBy: EventUserSchema,
})
export type SoftDeleteEvent = z.infer<typeof SoftDeleteEventSchema>

// --- Client list query (outbound GET params; not the API coerce/default parser) ---
export const ClientListEventsParamsSchema = z.object({
    limit: z.number().optional(),
    page: z.number().optional(),
    source: z.enum(['crm']).optional(),
    tab: z.enum(['overview', 'finance']).optional(),
    type: z.string().optional(),
})
export type ListEventsParams = z.infer<typeof ClientListEventsParamsSchema>

// --- Paginated list response ---
export const PaginatedEventsSchema = z.object({
    currentPage: z.number(),
    data: z.array(EventSchema),
    hasNextPage: z.boolean(),
    hasPreviousPage: z.boolean(),
    totalCount: z.number(),
    totalPages: z.number(),
})

// --- Aliases for Beehive consumers (match former fetcher names) ---
export type AccountEvent = z.infer<typeof EventSchema>
export type EventRevision = z.infer<typeof RevisionSchema>
export type PaginatedEvents = z.infer<typeof PaginatedEventsSchema>
export type CreateEventPayload = CreateEvent
export type UpdateEventPayload = UpdateEvent
export type DeleteEventPayload = SoftDeleteEvent

export const PinEventSchema = z.object({
    pinned: z.boolean(),
})
export type PinEventPayload = z.infer<typeof PinEventSchema>
